import { excerptText } from "../utils/dom.js";
import { tc } from "../i18n/index.js";
import { tr } from "../i18n/agents.js";
import type { BlockRecord } from "../types/index.js";

export const STORAGE_KEY = "sfcr-demo-v2";

let seed = null;
let state = null;

export async function initState() {
  seed = await fetch("/archive/report-state.json").then((response) => response.json());
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) {
    try {
      state = normalizeState(JSON.parse(saved));
      return state;
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }
  state = createInitialState(seed);
  saveState();
  return state;
}

export function getState() {
  return state;
}

export function getSeed() {
  return seed;
}

export function saveState() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function resetDemo() {
  state = createInitialState(seed);
  saveState();
  return state;
}

export function validatePlan() {
  state.phase = "report_production";
  state.planReview.status = "validated";
  // Garantir un rapport initialisé strictement vide : chaque bloc repart à
  // "à produire" sans texte/tableau résiduel, quelle que soit l'origine d'un
  // éventuel contenu pré-rempli (seed, import, état antérieur).
  for (const block of state.millesimes?.[0]?.blocks || []) {
    state.demo.blocks[block.id] = emptyRecord(block);
  }
  addGlobalEvent(tr("Humain: Romain Nobis"), tr("Plan global valide"), tr("Le rapport est initialise vide pour la production bloc par bloc."));
  saveState();
}

export function setPlanDecision(id, decision) {
  const modification = state.planReview.modifications.find((item) => item.id === id);
  if (!modification) return;
  modification.decision = decision;
  saveState();
}

export function pendingPlanDecisions() {
  return (state.planReview.modifications || []).filter((item) => !item.decision).length;
}

export function blocks() {
  return state.millesimes[0].blocks;
}

export function blockRecord(id: string): BlockRecord {
  return state.demo.blocks[id];
}

export function blockById(id) {
  return blocks().find((block) => block.id === id);
}

export function typologyFor(block) {
  return state.typologies.find((typology) => typology.id === block.type || (typology.appliesTo || []).includes(block.code));
}

export function personById(id) {
  return state.people.find((person) => person.id === id);
}

export function docById(id) {
  return state.kbDocuments.find((doc) => doc.id === id);
}

export function articleById(id) {
  return state.regulatoryArticles.find((article) => article.id === id);
}

export function refLabel(id) {
  if (!id) return "";
  const article = articleById(id);
  if (!article) return id.replace("art-", "Art. ");
  return article.code || tc(`articleTitle.${article.id}`, article.title) || id;
}

export function updateBlockRecord(id, patch) {
  state.demo.blocks[id] = { ...state.demo.blocks[id], ...patch };
  saveState();
}

export function setBlockStatus(id, status) {
  state.demo.blocks[id].status = status;
  saveState();
}

export function addAudit(blockId, actor, action, detail) {
  const record = blockRecord(blockId);
  record.auditLog.push({
    id: `ev-local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    timestamp: new Date().toISOString(),
    actor,
    action,
    detail
  });
  saveState();
}

export function addGlobalEvent(actor, action, detail) {
  state.demo.globalAudit.push({
    id: `ev-global-${Date.now()}`,
    timestamp: new Date().toISOString(),
    actor,
    action,
    detail
  });
}

export function saveAdminItem(section, id, nextItem) {
  const collection = adminCollection(section);
  const index = collection.findIndex((item) => item.id === id);
  if (index >= 0) {
    collection[index] = nextItem;
    saveState();
  }
}

// An ingested PDF joins the same knowledge base as the seeded documents, so it
// can be linked to a block as a source without any conversion.
export function addKbDocument(document) {
  state.kbDocuments = [document, ...state.kbDocuments.filter((item) => item.id !== document.id)];
  saveState();
  return document;
}

// Sources are what a block may be drafted from, so changing them invalidates
// nothing already produced: existing text, tables and citations are kept and
// the next run simply sees a different set of documents.
export function setBlockSources(blockId, docIds) {
  const block = blockById(blockId);
  if (!block) return;
  const known = new Set(getState().kbDocuments.map((doc) => doc.id));
  block.sourceDocIds = docIds.filter((id) => known.has(id));
  saveState();
  return block.sourceDocIds;
}

export function adminCollection(section) {
  return {
    agents: state.agents,
    typologies: state.typologies,
    "knowledge-base": state.kbDocuments,
    regulatory: state.regulatoryArticles,
    raci: state.people
  }[section] || [];
}

export function citationById(blockId, citationId) {
  return blockRecord(blockId)?.citations?.find((citation) => citation.id === citationId);
}

function createInitialState(base) {
  const next = structuredClone(base);
  next.phase = "plan_review";
  next.planReview = buildPlanReview(next);
  next.demo = {
    blocks: Object.fromEntries(next.millesimes[0].blocks.map((block) => [block.id, emptyRecord(block)])),
    globalAudit: []
  };
  return normalizeState(next);
}

function normalizeState(next) {
  if (!next.demo) {
    next.demo = { blocks: {}, globalAudit: [] };
  }
  if (!next.phase) next.phase = "plan_review";
  if (!next.planReview) next.planReview = buildPlanReview(next);
  if (!next.planReview.modifications) {
    const fresh = buildPlanReview(next);
    next.planReview.modifications = fresh.modifications;
    next.planReview.summary = fresh.summary;
  }
  for (const block of next.millesimes?.[0]?.blocks || []) {
    if (!next.demo.blocks[block.id]) next.demo.blocks[block.id] = emptyRecord(block);
    const record = next.demo.blocks[block.id];
    record.citations ||= [];
    record.chat ||= [];
    record.auditLog ||= [];
    record.agentRun ||= null;
    record.auditFindings ||= [];
    record.sourceGaps ||= [];
    if (record.aiExecution?.provider === "azure_openai") {
      record.sourceGaps.push(...record.auditFindings.filter(item => item.kind === "source").map(item => item.detail));
      record.sourceGaps = [...new Set(record.sourceGaps)];
      record.auditFindings = record.auditFindings.filter(item => item.kind !== "source");
      if (record.status === "audit_ko" && !record.auditFindings.length) record.status = "produced";
    }
    const technical = record.auditFindings.find(item => item.id === "azure-error");
    if (technical) {
      record.generationError = technical.detail;
      record.auditFindings = record.auditFindings.filter(item => item.id !== "azure-error");
      if (record.status === "audit_ko" && !record.auditFindings.length) record.status = record.text ? "produced" : "empty";
    }
    record.escalationOpen ||= false;
    record.manualEdited ||= false;
    record.manualEditedAt ||= null;
    record.manualEditKind ||= null;
    record.producedByEscalation ||= false;
    record.pendingEscalationReply ||= null;
  }
  next.demo.globalAudit ||= [];
  return next;
}

function emptyRecord(block): BlockRecord {
  return {
    status: "empty",
    text: "",
    table: [],
    citations: [],
    chat: [],
    auditLog: [],
    auditFindings: [],
    agentRun: null,
    manualEdited: false,
    manualEditedAt: null,
    manualEditKind: null,
    producedByEscalation: false,
    pendingEscalationReply: null,
    escalationSent: false,
    escalationOpen: false,
    escalationPersonId: block.assignee || null,
    sourceDocIds: block.sourceDocIds || []
  };
}

function buildPlanReview(next) {
  const blocksList = next.millesimes[0].blocks;
  return {
    status: "draft",
    summary: {
      chapters: blocksList.filter((block) => block.level === 0).length,
      subParts: blocksList.filter((block) => block.level > 0).length,
      reprisesN1: blocksList.length - 1,
      modifications: 1,
      blockCount: blocksList.length,
      typologyCount: next.typologies.length,
      sourceCount: next.kbDocuments.length
    },
    modifications: [
      {
        id: "mod-b7-outsourcing",
        blockCode: "B.7",
        title: "Mise à jour proposée par l'Architecte",
        detail: "Un nouveau prestataire (gestion des sinistres mobilité) est entré au périmètre en septembre 2025. À confirmer avec le secrétariat général avant publication.",
        decision: null,
        acceptLabel: "Accepter la modification",
        rejectLabel: "Conserver le bloc N-1 inchangé"
      }
    ],
    proposals: [
      {
        id: "prop-a3-table",
        blockCode: "A.3",
        title: "Ventilation du resultat financier par categorie CIC",
        rationale: "Ajout recommande pour satisfaire l'article 295 et eviter un KO reglementaire.",
        decision: "acceptable"
      },
      {
        id: "prop-b3-merge",
        blockCode: "B.3",
        title: "Fusionner ORSA et gestion des risques dans un seul bloc",
        rationale: "Refuse: la structure SFCR attend une sous-partie dediee et tracable.",
        decision: "not_acceptable"
      },
      {
        id: "prop-e2-focus",
        blockCode: "E.2",
        title: "Ajouter une synthese des variations SCR par module",
        rationale: "Acceptable si les QRT S.25.01 et le RSR detaille sont bien rattaches.",
        decision: "acceptable"
      }
    ],
    conflicts: [
      {
        id: "conf-a3-art295",
        severity: "high",
        blockCode: "A.3",
        title: "Article 295 incomplet dans le SFCR N-1",
        detail: "Le rapport N-1 ne ventile pas suffisamment les revenus par categorie d'actif."
      },
      {
        id: "conf-d2-source",
        severity: "medium",
        blockCode: "D.2",
        title: "Sources provisionnement multiples",
        detail: "Note de provisionnement et fonction actuarielle portent des hypotheses proches mais pas strictement identiques."
      }
    ],
    tree: blocksList.map((block) => ({
      id: block.id,
      code: block.code,
      title: block.title,
      level: block.level,
      type: block.type
    }))
  };
}

export function sourceExcerpt(doc) {
  const chunk = (doc?.excerptChunks || [])[0];
  return excerptText(chunk) || tc("state.noExcerpt", "Extrait source non renseigne dans la capture.");
}

export function sourceText(doc) {
  return (doc?.excerptChunks || []).map(chunk => typeof chunk === "string" ? chunk
    : chunk?.text || chunk?.content || chunk?.excerpt || "").filter(text => typeof text === "string" && text.trim()).join("\n\n");
}
